// Copyright 2025-2026 - Hewlett Packard Enterprise Company
import React, { useRef, useEffect, useContext, useState } from 'react';
import { Box, Text } from 'grommet';
import { GenAIFill } from 'grommet-icons/icons/GenAIFill';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../../contexts/AuthContext';
import ChatQuery from './ChatQuery';
import ChatAnswer from './ChatAnswer';
import { ChatContext } from '../../providers/ChatProvider';
import ChatQuestionButton from './ChatQuestionButton';
import TextEmphasis from '../TextEmphasis/TextEmphasis';
import useFlags from '../../hooks/useFlags';

const ChatMainContent = () => {
  const auth = useContext(AuthContext);
  const [userName, setUserName] = useState();
  const chatBodyRef = useRef();
  const { conversations, handleQueryClick } = useContext(ChatContext);
  const { t } = useTranslation('common');
  const flags = useFlags();

  const metricsEnabled =
    flags.T_20250507_64906_CLICKHOUSE_QUERY_CHATBOT_SUPPORT_BOOL();
  const fwBaselinesEnabled =
    flags.T_20251008_60053_FIRMWARE_BASELINES_SUPPORT_BOOL();

  const baseStarterQuestions = t('chat.starter_questions', {
    returnObjects: true,
  });
  const additionalStarterQuestion = t('chat.additional_starter_question', {
    returnObjects: true,
  });
  const metricsStarterQuestion = t('chat.metrics_starter_question', {
    returnObjects: true,
  });
  const fwBaselinesStarterQuestion = t('chat.fw_baselines_starter_question', {
    returnObjects: true,
  });

  // Showing starter questions based on LD flag
  const starterQuestions = [
    ...baseStarterQuestions,
    ...(metricsEnabled ? metricsStarterQuestion : []),
    ...(fwBaselinesEnabled ? fwBaselinesStarterQuestion : []),
    ...(!metricsEnabled && !fwBaselinesEnabled
      ? additionalStarterQuestion
      : []),
  ];

  const scrollToTop = () => {
    chatBodyRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    setTimeout(() => {
      requestAnimationFrame(() => {
        scrollToTop();
      });
    }, 0);
  }, [handleQueryClick]);

  useEffect(() => {
    let isMounted = true;

    auth
      .getUserName()
      .then(name => {
        isMounted && setUserName(name);
      })
      .catch(e => {
        if (isMounted) {
          setUserName('');
        }
      });

    return () => {
      isMounted = false;
    };
  }, [auth]);

  return (
    <Box
      pad="medium"
      align="start"
      data-testid="chat-main-content"
      overflow="auto"
      flex="grow"
      fill="vertical"
    >
      {conversations.length === 0 && (
        <Box gap="medium" margin={{ bottom: 'medium' }}>
          <GenAIFill color="brand" size="xxlarge" />
          <TextEmphasis size="xlarge" margin={{ bottom: 'xsmall' }}>
            {userName
              ? t('chat.chat_greeting', { name: userName })
              : t('chat.chat_greeting_no_name')}
          </TextEmphasis>
          <Text size="medium" margin={{ bottom: 'xsmall' }}>
            {t('chat.chat_starter_text')}
          </Text>
          {starterQuestions.map(q => (
            <ChatQuestionButton
              question={q}
              onClick={() => {
                handleQueryClick(q);
              }}
            />
          ))}
          <Text>{t('chat.chat_feedback_text')}</Text>
        </Box>
      )}

      {conversations.map((exchange, index) => (
        <Box
          key={index}
          width="100%"
          pad={{ vertical: 'small' }}
          margin={{ bottom: 'small' }}
          style={{ maxWidth: '100%' }}
          flex={false}
        >
          <div ref={chatBodyRef} />
          <ChatQuery query={exchange.query} />
          <ChatAnswer
            query={exchange.query}
            answer={exchange.answer}
            showFollowUpQuestions={exchange.showFollowUpQuestions}
            showFeedback={exchange.showFeedback}
          />
        </Box>
      ))}
    </Box>
  );
};

export default ChatMainContent;
